/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useMemo } from 'react';
import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { AssetsMenuItemFreeUsersPlacement } from '@jira/growth__assets-menu-item-free-users-placement/src/AssetsMenuItemFreeUsersPlacement.tsx';
import { isTeamsAppEnabled } from '@jira/platform__teams-app-config/src/index.tsx';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import { useGoals } from '@atlassian/global-shortcuts/goals';
import { useConfluence } from '@atlassian/global-shortcuts/confluence';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/common/types';
import { GLOBAL_APP_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useLoom } from '@atlassian/global-shortcuts/loom';
import { useAssets } from '@atlassian/global-shortcuts/assets';
import { useOpsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { useProjects } from '@atlassian/global-shortcuts/projects';
import { GlobalAppShortcuts } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-global-apps/src/index.tsx';

import {
	getLicensedProducts,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useTeamsAppShortcut } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/ui/configurable-menu-items/global-shortcuts/apps/teams/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { CustomItemProps } from '@atlassian/navigation-common/types';
import { useShortcutFallbackUrls } from './useShortcutFallbackUrls.tsx';
import { CustomShortcutItem } from './CustomShortcutItem.tsx';

export function useShortcuts(): ShortcutsConfig {
	const licenceStates = useLicenseStates();
	const products = getLicensedProducts();
	const fallbackUrls = useShortcutFallbackUrls();
	const appEditionsValue = useAppEditions();
	const isJsmFree = appEditionsValue?.serviceDesk === FREE_EDITION;
	const isSiteAdmin = useIsSiteAdmin();
	const showTeams = isTeamsAppEnabled();
	const { config: teamsAppConfig } = useTeamsAppShortcut();

	// cross-unit Assets host resolution.
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();
	const workspaceHostState = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});
	const isAssetsResolverSuccess =
		workspaceHostState.status === 'success' && fg('astral_units_workspace_host_resolver');
	const resolvedAssetsUrl = isAssetsResolverSuccess
		? workspaceHostState.data.toAbsoluteUrl(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_ASSETS])
		: fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_ASSETS];

	/**
	 * Base "do we show it" logic based on:
	 * - fast meta (HTML <meta> tags, which are available in SSR render and fed into here from getLicensedProducts)
	 * - other checks as seen in Nav4's useLicenseFilterMap()
	 */
	let showConfluence = products?.[LICENSED_PRODUCTS.CONFLUENCE];
	let showLoom = products[LICENSED_PRODUCTS.LOOM];
	const showAssets = licenceStates?.serviceDesk === 'ACTIVE' || isAssetsResolverSuccess;
	const showOpsGenie = licenceStates?.serviceDesk === 'ACTIVE';
	// The metadata used in getLicensedProducts() was previously "townsquare" but is now "project" and "goal".
	// This was copied from useLicenseFilterMap() in which it was noted to now use PROJECT/GOAL metadata with TOWNSQUARE fallback.
	let showProjects: boolean = true;
	showProjects = !!products[LICENSED_PRODUCTS.PROJECT] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];
	let showGoals: boolean = true;
	showGoals = !!products[LICENSED_PRODUCTS.GOAL] || !!products[LICENSED_PRODUCTS.TOWNSQUARE];

	// Some extra logic for some specific cases.
	// Consolidated from useLicenseFilterMap()
	if (licenceStates?.serviceDesk === 'ACTIVE' || licenceStates?.productDiscovery === 'ACTIVE') {
		showConfluence = licenceStates?.core === 'ACTIVE';
		showLoom = licenceStates?.core === 'ACTIVE';
	}

	/**
	 * Item components
	 */
	const goals = useGoals(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_GOALS], {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_GOALS,
	});
	const confluence = useConfluence(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_CONFLUENCE], {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_CONFLUENCE,
	});
	const loom = useLoom(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_LOOM], {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_LOOM,
	});
	const opsgenie = useOpsgenie(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_OPSGENIE], {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_OPSGENIE,
	});
	const assetsBase = useAssets(resolvedAssetsUrl, {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_ASSETS,
	});
	// bypass platform AppSwitcher BFF filter via component type.
	const assets = useMemo(
		() =>
			licenceStates?.serviceDesk === 'ACTIVE' || isAssetsResolverSuccess
				? {
						...assetsBase,
						type: 'component' as const,
						component: () => <GlobalAppShortcuts menuId={GLOBAL_APP_MENU_ID.GLOBAL_APP_ASSETS} />,
					}
				: assetsBase,
		[assetsBase, licenceStates?.serviceDesk, isAssetsResolverSuccess],
	);
	const projects = useProjects(fallbackUrls[GLOBAL_APP_MENU_ID.GLOBAL_APP_PROJECTS], {
		menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_PROJECTS,
	});

	/**
	 * Final configuration
	 */
	return useMemo(() => {
		return {
			...(showConfluence ? { [GLOBAL_APP_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence } : {}),
			...(showLoom && { [GLOBAL_APP_MENU_ID.GLOBAL_APP_LOOM]: loom }),
			...(showAssets && {
				[GLOBAL_APP_MENU_ID.GLOBAL_APP_ASSETS]: {
					...assets,
					...(isSiteAdmin &&
						isJsmFree &&
						fg('sqa-2098-assets-nav-upsell-jsm-free-nav') && {
							type: 'component',
							component: () => <AssetsMenuItemFreeUsersPlacement />,
						}),
				},
			}),
			...(showOpsGenie && { [GLOBAL_APP_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie }),
			...(showTeams && {
				[GLOBAL_APP_MENU_ID.GLOBAL_APP_TEAMS]: {
					...teamsAppConfig,
					menuItemId: GLOBAL_APP_MENU_ID.GLOBAL_APP_TEAMS,
					type: 'component',
					isReorderable: true,
					component: (props?: CustomItemProps) => (
						<CustomShortcutItem menuId={GLOBAL_APP_MENU_ID.GLOBAL_APP_TEAMS} {...props} />
					),
				},
			}),
			...(showGoals && { [GLOBAL_APP_MENU_ID.GLOBAL_APP_GOALS]: goals }),
			...(showProjects && { [GLOBAL_APP_MENU_ID.GLOBAL_APP_PROJECTS]: projects }),
		};
	}, [
		showConfluence,
		confluence,
		showLoom,
		loom,
		showAssets,
		assets,
		isSiteAdmin,
		isJsmFree,
		showOpsGenie,
		opsgenie,
		showTeams,
		teamsAppConfig,
		showGoals,
		goals,
		showProjects,
		projects,
	]);
}
